import { createContext, FC, ReactNode, useContext, useMemo } from "react";
import { useIntl } from "react-intl";
import { Box, CircularProgress } from "@mui/material";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import useBrandingConfig from "../../Hooks/useBrandingConfig";
import { BrandingMode, buildBrandingTheme, seedDefaultBranding } from "../../utils/branding/brandingTheme";
import { darkThemeConfig, defaultThemeConfig } from "../../utils/muiUtils";
import { MessageContent, TApplicationUIConfigForm } from "../../utils/branding/uiConfig.types";

export interface BrandingContentValue {
    login: MessageContent | null;
    registration: MessageContent | null;
}

const EMPTY_CONTENT: BrandingContentValue = { login: null, registration: null };

const BrandingContentContext = createContext<BrandingContentValue>(EMPTY_CONTENT);

// Branded title/subtitle/description for the Login/Register headers. Returns nulls when branding is off,
// so consumers fall back to today's i18n copy. Available to any component under <BrandingThemeProvider>.
export const useBrandingContent = (): BrandingContentValue => useContext(BrandingContentContext);

interface BrandingThemeProviderProps {
    children: ReactNode;
    // NG-8456 live preview: when passed, the provider renders from this unsaved draft instead of reading
    // the `?brandingId=` URL param and fetching. This is what guarantees "preview == production" — the
    // preview reuses the exact theme + content wiring below rather than re-implementing it. Passing the
    // prop at all switches to preview mode; `null` (or `isEnabled: false`) previews the off/default state.
    previewConfig?: TApplicationUIConfigForm | null;
    // Which palette to preview; defaults to light. Ignored outside preview mode (mode comes from the URL).
    previewMode?: BrandingMode;
}

// Wraps the Login/Register routes. When an enabled branding config is present it applies a scoped MUI
// theme (authenticated app theme untouched) and exposes the branded content; otherwise it renders the
// children unchanged under the default theme. While a referenced config is loading it holds the page to
// avoid a flash of the default theme before the branded one applies.
const BrandingThemeProvider: FC<BrandingThemeProviderProps> = ({ children, previewConfig, previewMode }) => {
    const baseTheme = useTheme();
    const { formatMessage } = useIntl();
    // Preview mode is active whenever the prop is supplied (even as null → preview the off state).
    const isPreview = previewConfig !== undefined;
    // Always called (Rules of Hooks); `skip` disables the URL read + fetch entirely in preview mode.
    const fetched = useBrandingConfig({ skip: isPreview });

    const brandingId = isPreview ? null : fetched.brandingId;
    const isLoading = isPreview ? false : fetched.isLoading;
    const mode = isPreview ? (previewMode ?? "light") : fetched.mode;
    const isEnabled = isPreview ? !!previewConfig?.isEnabled : fetched.isEnabled;
    const config = isPreview ? previewConfig : fetched.config;

    // The branded base must match the BRANDING mode, not the ambient app theme: the app theme follows the
    // user's color-mode toggle (which persists through logout), while the branded page's mode comes from
    // the config/URL/OS alone. buildThemeConfig bakes concrete palette hexes into component styleOverrides
    // that the branding slots can't reach (dialog/paper fills, baked typography ink, action overlays), so a
    // mismatched base bleeds the other mode's styling into the branded page — and into the NG-8598 preview
    // sandbox, breaking "preview == production". Same construction as ThemedApp in App.tsx.
    const brandedBase = useMemo(() => createTheme(mode === "dark" ? darkThemeConfig : defaultThemeConfig), [mode]);
    const brandedTheme = useMemo(
        () => (isEnabled && config ? buildBrandingTheme(brandedBase, config.colors[mode], mode) : null),
        [isEnabled, config, mode, brandedBase],
    );

    // Off state: seed `palette.branding` with theme-derived defaults so the page styles read the slots
    // uniformly whether branding is on or off (no duplicated literal fallbacks). This also seeds the one
    // standard channel the migrated neutral borders consume — `divider` — so the OTP/card outlines keep
    // today's look; every other standard channel (background/text/error/…) is left untouched. See
    // seedDefaultBranding for the exact set.
    const defaultTheme = useMemo(() => seedDefaultBranding(baseTheme), [baseTheme]);

    const content = useMemo<BrandingContentValue>(
        () => (isEnabled && config ? { login: config.content.login, registration: config.content.registration } : EMPTY_CONTENT),
        [isEnabled, config],
    );

    if (brandingId && isLoading) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress aria-label={formatMessage({ id: "branding.label.loading", defaultMessage: "Loading branding" })} />
            </Box>
        );
    }

    // Branded config → its scoped theme; otherwise the seeded default theme (palette.branding always present).
    return (
        <ThemeProvider theme={brandedTheme ?? defaultTheme}>
            <BrandingContentContext.Provider value={content}>{children}</BrandingContentContext.Provider>
        </ThemeProvider>
    );
};

export default BrandingThemeProvider;
